import { useCallback, type MouseEvent } from "react";
import { useLocation, useNavigate, type Location } from "react-router";
import { ROUTE_PATTERNS } from "../../../shared/routing/appPaths";
import { useUserPreferences } from "../../profile/preferences/userPreferences";
import { helpPlaceHref, type HelpPlace } from "../helpArticles";

/**
 * How help opens and closes (web-app.md WA-6). Personal preferences say whether it opens in
 * its own tab (the default) or in place of the page. Its own tab is one named tab, so the
 * app never opens a second; help in place remembers the page it was opened from. Closing
 * help, by "?" or its close control, closes a tab the app opened and returns to the app's
 * tab, and otherwise goes back to that page.
 */
export const HELP_WINDOW_NAME = "dmw-help";

/** Location state of help opened in place: where closing it returns to. */
export interface HelpReturn {
  readonly helpReturnTo: string;
}

export function helpReturnOf(state: unknown): HelpReturn | null {
  return typeof state === "object" && state !== null && typeof (state as Partial<HelpReturn>).helpReturnTo === "string"
    ? (state as HelpReturn)
    : null;
}

function addressOf(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

function isPlainClick(event: MouseEvent): boolean {
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export interface HelpLinkProps {
  readonly href: string;
  readonly target?: string;
  readonly onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export interface HelpOpener {
  /** Opens help at `place`, as Personal preferences say. */
  readonly open: (place: HelpPlace) => void;
  /** A link's attributes that open help at `place` the same way; a modified click is the browser's. */
  readonly linkProps: (place: HelpPlace) => HelpLinkProps;
}

export function useHelpOpener(): HelpOpener {
  const navigate = useNavigate();
  const location = useLocation();
  const { preferences } = useUserPreferences();
  const inNewTab = preferences.openHelpInNewTab;
  // Inside help, help keeps the page it was first opened from.
  const helpReturnTo = helpReturnOf(location.state)?.helpReturnTo ?? addressOf(location);

  const open = useCallback((place: HelpPlace) => {
    const href = helpPlaceHref(place);
    if (inNewTab) {
      window.open(href, HELP_WINDOW_NAME)?.focus();
    } else {
      void navigate(href, { state: { helpReturnTo } satisfies HelpReturn });
    }
  }, [inNewTab, navigate, helpReturnTo]);

  const linkProps = useCallback((place: HelpPlace): HelpLinkProps => {
    const href = helpPlaceHref(place);
    return inNewTab
      ? { href, target: HELP_WINDOW_NAME }
      : {
          href,
          onClick: (event) => {
            if (!isPlainClick(event)) return;
            event.preventDefault();
            void navigate(href, { state: { helpReturnTo } satisfies HelpReturn });
          },
        };
  }, [inNewTab, navigate, helpReturnTo]);

  return { open, linkProps };
}

/** Whether this window is the help tab the app opened, with the app still open beside it. */
function openerOfHelpTab(): Window | null {
  const opener = window.opener as Window | null;
  if (window.name !== HELP_WINDOW_NAME || opener === null || opener.closed) return null;
  try {
    return opener.location.origin === window.location.origin ? opener : null;
  } catch {
    return null;
  }
}

/** Leaves help: closes the help tab and returns to the app's, or goes back to the page help was opened from. */
export function useHelpClose(): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    const opener = openerOfHelpTab();
    if (opener !== null) {
      opener.focus();
      window.close();
      // A browser that refuses to close the tab leaves it open: then help goes back in place.
      if (window.closed) return;
    }
    void navigate(helpReturnOf(location.state)?.helpReturnTo ?? ROUTE_PATTERNS.landing);
  }, [navigate, location.state]);
}
